import React, { useState, useEffect } from 'react';
import { Bell, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { apiService } from '../services/apiService';
import { format } from 'date-fns';

function NovedadesPanel({ plantaId }) {
  const [novedades, setNovedades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNovedades();
    const interval = setInterval(loadNovedades, 15000);
    return () => clearInterval(interval);
  }, [plantaId]);

  const loadNovedades = async () => {
    try {
      const data = await apiService.getNovedades(plantaId);
      setNovedades(data);
    } catch (error) {
      console.error('Error cargando novedades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarLeida = async (novedadId) => {
    try {
      await apiService.marcarNovedadLeida(novedadId, 'Operador');
      loadNovedades();
    } catch (error) {
      console.error('Error marcando novedad:', error);
    }
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      operativa: Bell,
      mantenimiento: AlertCircle,
      falla: AlertCircle,
      evento: Bell,
      otro: Bell
    };
    return icons[tipo] || Bell;
  };

  const getTipoColor = (tipo) => {
    const colors = {
      operativa: 'bg-blue-100 text-blue-800',
      mantenimiento: 'bg-yellow-100 text-yellow-800',
      falla: 'bg-red-100 text-red-800',
      evento: 'bg-green-100 text-green-800',
      otro: 'bg-gray-100 text-gray-800'
    };
    return colors[tipo] || colors.otro;
  };

  if (loading) {
    return <div className="card">Cargando novedades...</div>;
  }

  const novedadesNoLeidas = novedades.filter(n => !n.leida);
  const novedadesLeidas = novedades.filter(n => n.leida);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Novedades</h2>
        {novedadesNoLeidas.length > 0 && (
          <div className="badge badge-error">
            {novedadesNoLeidas.length} sin leer
          </div>
        )}
      </div>

      {/* Novedades no leídas */}
      {novedadesNoLeidas.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Sin Leer</h3>
          <div className="space-y-3">
            {novedadesNoLeidas.map((novedad) => (
              <NovedadCard
                key={novedad.id}
                novedad={novedad}
                onMarcarLeida={handleMarcarLeida}
                getTipoIcon={getTipoIcon}
                getTipoColor={getTipoColor}
              />
            ))}
          </div>
        </div>
      )}

      {/* Novedades leídas */}
      {novedadesLeidas.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Leídas</h3>
          <div className="space-y-3 opacity-60">
            {novedadesLeidas.map((novedad) => (
              <NovedadCard
                key={novedad.id}
                novedad={novedad}
                onMarcarLeida={null}
                getTipoIcon={getTipoIcon}
                getTipoColor={getTipoColor}
              />
            ))}
          </div>
        </div>
      )}

      {novedades.length === 0 && (
        <div className="card text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Sin Novedades
          </h3>
          <p className="text-gray-600">
            No hay novedades registradas
          </p>
        </div>
      )}
    </div>
  );
}

function NovedadCard({ novedad, onMarcarLeida, getTipoIcon, getTipoColor }) {
  const Icon = getTipoIcon(novedad.tipo_novedad);

  return (
    <div className={`card ${!novedad.leida ? 'border-l-4 border-primary-600' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <div className={`p-2 rounded-lg ${getTipoColor(novedad.tipo_novedad)}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-semibold text-gray-900">{novedad.titulo}</h3>
              <span className={`badge ${getTipoColor(novedad.tipo_novedad)}`}>
                {novedad.tipo_novedad}
              </span>
            </div>
            <p className="text-gray-700 mb-2">{novedad.descripcion}</p>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {format(new Date(novedad.timestamp), 'dd/MM/yyyy HH:mm:ss')}
              </span>
              {novedad.leida && novedad.leida_por && (
                <span className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Leída por {novedad.leida_por}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {!novedad.leida && onMarcarLeida && (
          <button
            onClick={() => onMarcarLeida(novedad.id)}
            className="btn btn-secondary ml-4"
          >
            Marcar como leída
          </button>
        )}
      </div>
    </div>
  );
}

export default NovedadesPanel;
